import React, { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, TrendingUp, TrendingDown, CreditCard, RefreshCw, 
  Calendar, AlertTriangle, CheckCircle2, Clock, Building2,
  ArrowUpRight, ArrowDownRight, Loader2, Plus, Edit2, Trash2,
  ChevronRight, PiggyBank, Wallet, Receipt, Settings, X
} from 'lucide-react';
import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CashFlowPage() {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [dailyProjections, setDailyProjections] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [confirmingExpense, setConfirmingExpense] = useState(null); // For suggestion confirmation with edits
  const [message, setMessage] = useState(null);
  const [timelineFilter, setTimelineFilter] = useState(30); // 7, 14, 30, 60, 90 days
  const [expandedDay, setExpandedDay] = useState(null);

  // New expense form state
  const [newExpense, setNewExpense] = useState({
    name: '',
    category: 'other',
    amount: '',
    frequency: 'monthly',
    next_due_date: '',
    vendor_name: '',
  });

  // Confirmation form state (for editing suggestion before confirming)
  const [confirmForm, setConfirmForm] = useState({
    name: '',
    category: 'other',
    amount: '',
    frequency: 'monthly',
    next_due_date: '',
    vendor_name: '',
  });

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/cashflow/status`);
      if (resp.ok) {
        const data = await resp.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/cashflow/dashboard`);
      if (resp.ok) {
        const data = await resp.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  }, []);

  const fetchRecurringExpenses = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/cashflow/recurring-expenses`);
      if (resp.ok) {
        const data = await resp.json();
        setRecurringExpenses(data.expenses || []);
      }
    } catch (err) {
      console.error('Error fetching recurring expenses:', err);
    }
  }, []);

  const fetchSuggestions = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/cashflow/recurring-expenses/suggestions`);
      if (resp.ok) {
        const data = await resp.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    }
  }, []);

  const fetchDailyProjections = useCallback(async (days = 90) => {
    try {
      const resp = await fetch(`${API_URL}/api/cashflow/daily-projections?days=${days}`);
      if (resp.ok) {
        const data = await resp.json();
        setDailyProjections(data);
      }
    } catch (err) {
      console.error('Error fetching daily projections:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStatus(),
        fetchDashboard(),
        fetchRecurringExpenses(),
        fetchSuggestions(),
        fetchDailyProjections(90),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchStatus, fetchDashboard, fetchRecurringExpenses, fetchSuggestions, fetchDailyProjections]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const resp = await fetch(`${API_URL}/api/cashflow/sync`, { method: 'POST' });
      if (resp.ok) {
        showMessage('success', 'Data synced successfully');
        await fetchDashboard();
        await fetchSuggestions();
        await fetchDailyProjections(timelineFilter);
      } else {
        showMessage('error', 'Sync failed');
      }
    } catch (err) {
      showMessage('error', 'Sync failed');
    }
    setSyncing(false);
  };

  const confirmExpense = async (expenseId) => {
    try {
      const resp = await fetch(`${API_URL}/api/cashflow/recurring-expenses/${expenseId}/confirm`, { method: 'POST' });
      if (resp.ok) {
        showMessage('success', 'Expense confirmed');
        await fetchRecurringExpenses();
        await fetchSuggestions();
        await fetchDashboard();
      }
    } catch (err) {
      showMessage('error', 'Failed to confirm expense');
    }
  };

  // Open confirmation modal with editable fields
  const openConfirmModal = (expense) => {
    setConfirmingExpense(expense);
    setConfirmForm({
      name: expense.name || '',
      category: expense.category || 'other',
      amount: expense.amount?.toString() || '',
      frequency: expense.frequency || 'monthly',
      next_due_date: expense.next_due_date || '',
      vendor_name: expense.vendor_name || expense.name || '',
    });
  };

  // Confirm with potential edits
  const confirmExpenseWithEdits = async () => {
    if (!confirmingExpense) return;
    
    try {
      // First update the expense with any edits
      const updateResp = await fetch(`${API_URL}/api/cashflow/recurring-expenses/${confirmingExpense.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: confirmingExpense.id,
          name: confirmForm.name,
          category: confirmForm.category,
          amount: parseFloat(confirmForm.amount),
          frequency: confirmForm.frequency,
          next_due_date: confirmForm.next_due_date,
          vendor_name: confirmForm.vendor_name,
          auto_detected: confirmingExpense.auto_detected,
          confirmed: true, // Mark as confirmed
        }),
      });
      
      if (updateResp.ok) {
        showMessage('success', 'Expense confirmed and saved');
        setConfirmingExpense(null);
        await fetchRecurringExpenses();
        await fetchSuggestions();
        await fetchDashboard();
      } else {
        showMessage('error', 'Failed to save expense');
      }
    } catch (err) {
      showMessage('error', 'Failed to confirm expense');
    }
  };

  // Open edit modal for confirmed expense
  const openEditModal = (expense) => {
    setEditingExpense(expense);
    setConfirmForm({
      name: expense.name || '',
      category: expense.category || 'other',
      amount: expense.amount?.toString() || '',
      frequency: expense.frequency || 'monthly',
      next_due_date: expense.next_due_date || '',
      vendor_name: expense.vendor_name || '',
    });
  };

  // Save edits to confirmed expense
  const saveExpenseEdits = async () => {
    if (!editingExpense) return;
    
    try {
      const resp = await fetch(`${API_URL}/api/cashflow/recurring-expenses/${editingExpense.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editingExpense.id,
          name: confirmForm.name,
          category: confirmForm.category,
          amount: parseFloat(confirmForm.amount),
          frequency: confirmForm.frequency,
          next_due_date: confirmForm.next_due_date,
          vendor_name: confirmForm.vendor_name,
          auto_detected: editingExpense.auto_detected,
          confirmed: true,
        }),
      });
      
      if (resp.ok) {
        showMessage('success', 'Expense updated');
        setEditingExpense(null);
        await fetchRecurringExpenses();
        await fetchDashboard();
      } else {
        showMessage('error', 'Failed to update expense');
      }
    } catch (err) {
      showMessage('error', 'Failed to update expense');
    }
  };

  const dismissExpense = async (expenseId) => {
    try {
      const resp = await fetch(`${API_URL}/api/cashflow/recurring-expenses/${expenseId}/dismiss`, { method: 'POST' });
      if (resp.ok) {
        showMessage('success', 'Suggestion dismissed');
        await fetchSuggestions();
      }
    } catch (err) {
      showMessage('error', 'Failed to dismiss');
    }
  };

  const deleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this recurring expense?')) return;
    try {
      const resp = await fetch(`${API_URL}/api/cashflow/recurring-expenses/${expenseId}`, { method: 'DELETE' });
      if (resp.ok) {
        showMessage('success', 'Expense deleted');
        await fetchRecurringExpenses();
        await fetchDashboard();
      }
    } catch (err) {
      showMessage('error', 'Failed to delete');
    }
  };

  const addManualExpense = async () => {
    if (!newExpense.name || !newExpense.amount || !newExpense.next_due_date) {
      showMessage('error', 'Please fill all required fields');
      return;
    }
    try {
      const resp = await fetch(`${API_URL}/api/cashflow/recurring-expenses/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newExpense,
          amount: parseFloat(newExpense.amount),
        }),
      });
      if (resp.ok) {
        showMessage('success', 'Expense added');
        setShowAddExpense(false);
        setNewExpense({ name: '', category: 'other', amount: '', frequency: 'monthly', next_due_date: '', vendor_name: '' });
        await fetchRecurringExpenses();
        await fetchDashboard();
      }
    } catch (err) {
      showMessage('error', 'Failed to add expense');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      payroll: 'bg-blue-100 text-blue-800',
      insurance: 'bg-purple-100 text-purple-800',
      software: 'bg-indigo-100 text-indigo-800',
      utilities: 'bg-yellow-100 text-yellow-800',
      rent: 'bg-emerald-100 text-emerald-800',
      subscriptions: 'bg-cyan-100 text-cyan-800',
      maintenance: 'bg-orange-100 text-orange-800',
      other: 'bg-slate-100 text-slate-800',
    };
    return colors[category] || colors.other;
  };

  const getProjectionTrend = (current, projected) => {
    if (projected > current) return { icon: TrendingUp, color: 'text-green-600', label: 'Growing' };
    if (projected < current) return { icon: TrendingDown, color: 'text-red-600', label: 'Declining' };
    return { icon: TrendingUp, color: 'text-slate-600', label: 'Stable' };
  };

  if (loading) {
    return (
      <AdminSidebar>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </AdminSidebar>
    );
  }

  const summary = dashboard?.summary || {};
  const bankBalances = dashboard?.bank_balances || {};
  const stripeBalance = dashboard?.stripe_balance || {};
  const loc = dashboard?.line_of_credit;
  const creditRec = dashboard?.credit_recommendation;
  const incomingPayments = dashboard?.incoming_payments || [];
  const outgoingPayments = dashboard?.outgoing_payments || [];

  return (
    <AdminSidebar>
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Cash Flow Manager</h1>
                <p className="text-sm text-slate-500 mt-1">
                  Real-time financial visibility and optimization
                </p>
              </div>
              <div className="flex items-center gap-3">
                {status?.last_sync && (
                  <span className="text-xs text-slate-500">
                    Last sync: {formatDate(status.last_sync)}
                  </span>
                )}
                <Button
                  onClick={handleSync}
                  disabled={syncing}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Sync Now
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Message Toast */}
        {message && (
          <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
            message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {message.text}
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Connection Status */}
          {status && (
            <div className="mb-6 flex flex-wrap gap-2">
              {['mercury', 'qbo', 'stripe', 'jobtread'].map(service => (
                <div
                  key={service}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                    status[service]?.connected
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${status[service]?.connected ? 'bg-green-500' : 'bg-red-500'}`} />
                  {service.toUpperCase()}
                </div>
              ))}
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Current Balance */}
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-600">Current Balance</span>
                <Wallet className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.current_balance)}</p>
              <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                <span>Bank: {formatCurrency(bankBalances.total)}</span>
                <span>Stripe: {formatCurrency(stripeBalance.available)}</span>
              </div>
            </div>

            {/* This Week Cashflow */}
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-600">This Week</span>
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-semibold text-green-600">+{formatCurrency(summary.incoming_this_week)}</span>
                <span className="text-lg font-semibold text-red-600">-{formatCurrency(summary.outgoing_this_week)}</span>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Net: {formatCurrency(summary.incoming_this_week - summary.outgoing_this_week)}
              </p>
            </div>

            {/* 30-Day Projection */}
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-600">30-Day Projection</span>
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.projected_30_day)}</p>
              {(() => {
                const trend = getProjectionTrend(summary.current_balance, summary.projected_30_day);
                const TrendIcon = trend.icon;
                return (
                  <p className={`mt-2 text-xs flex items-center gap-1 ${trend.color}`}>
                    <TrendIcon className="w-3 h-3" /> {trend.label}
                  </p>
                );
              })()}
            </div>

            {/* Line of Credit */}
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-600">Available Credit</span>
                <CreditCard className="w-5 h-5 text-amber-600" />
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {loc ? formatCurrency(loc.available_credit) : '—'}
              </p>
              {loc && (
                <p className="mt-2 text-xs text-slate-500">
                  Balance: {formatCurrency(loc.current_balance)}
                </p>
              )}
            </div>
          </div>

          {/* Credit Recommendation Alert */}
          {creditRec && creditRec.action !== 'hold' && (
            <div className={`mb-6 rounded-xl p-4 ${
              creditRec.urgency === 'high' ? 'bg-red-50 border border-red-200' :
              creditRec.urgency === 'medium' ? 'bg-amber-50 border border-amber-200' :
              'bg-blue-50 border border-blue-200'
            }`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  creditRec.urgency === 'high' ? 'text-red-600' :
                  creditRec.urgency === 'medium' ? 'text-amber-600' :
                  'text-blue-600'
                }`} />
                <div>
                  <h3 className="font-semibold text-slate-900">
                    {creditRec.action === 'tap_credit' ? 'Consider Tapping Credit Line' : 'Consider Paying Down Credit'}
                  </h3>
                  <p className="text-sm text-slate-700 mt-1">{creditRec.reason}</p>
                  <p className="text-sm font-medium mt-2">
                    Suggested amount: {formatCurrency(creditRec.amount)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="bg-white rounded-t-xl border-t border-l border-r">
            <nav className="flex gap-1 p-1 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: Wallet },
                { id: 'timeline', label: 'Timeline', icon: Calendar },
                { id: 'incoming', label: 'Incoming', icon: ArrowDownRight },
                { id: 'outgoing', label: 'Outgoing', icon: ArrowUpRight },
                { id: 'recurring', label: 'Recurring', icon: Receipt },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-800'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-b-xl shadow-sm border-l border-r border-b p-6">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Projections Chart */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-4">Cash Flow Projections</h3>
                  <div className="space-y-4">
                    {[
                      { label: '30 Days', value: summary.projected_30_day },
                      { label: '60 Days', value: summary.projected_60_day },
                      { label: '90 Days', value: summary.projected_90_day },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <span className="text-sm text-slate-600 w-20">{item.label}</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${item.value >= 0 ? 'bg-blue-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(100, Math.abs(item.value) / (summary.current_balance || 1) * 50)}%` }}
                          />
                        </div>
                        <span className={`text-sm font-medium w-28 text-right ${item.value >= 0 ? 'text-slate-900' : 'text-red-600'}`}>
                          {formatCurrency(item.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bank Balances */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-4">Account Balances</h3>
                  <div className="space-y-3">
                    {/* Mercury Accounts - Dynamic list from API */}
                    {bankBalances.accounts && bankBalances.accounts.length > 0 ? (
                      bankBalances.accounts.map((account, idx) => (
                        <div key={account.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {account.type?.includes('checking') || account.name?.toLowerCase().includes('operating') ? (
                              <Building2 className="w-5 h-5 text-slate-600" />
                            ) : account.type?.includes('savings') ? (
                              <PiggyBank className="w-5 h-5 text-green-600" />
                            ) : account.type?.includes('credit') || account.name?.toLowerCase().includes('credit') ? (
                              <CreditCard className="w-5 h-5 text-purple-600" />
                            ) : (
                              <Wallet className="w-5 h-5 text-slate-600" />
                            )}
                            <div>
                              <span className="text-sm font-medium">{account.name || 'Mercury Account'}</span>
                              <span className="text-xs text-slate-500 ml-2">({account.type || 'account'})</span>
                            </div>
                          </div>
                          <span className="font-semibold">{formatCurrency(account.balance)}</span>
                        </div>
                      ))
                    ) : (
                      <>
                        {/* Fallback: Show summary if no individual accounts */}
                        {bankBalances.checking > 0 && (
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Building2 className="w-5 h-5 text-slate-600" />
                              <span className="text-sm font-medium">Mercury Checking</span>
                            </div>
                            <span className="font-semibold">{formatCurrency(bankBalances.checking)}</span>
                          </div>
                        )}
                        {bankBalances.savings > 0 && (
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <PiggyBank className="w-5 h-5 text-slate-600" />
                              <span className="text-sm font-medium">Mercury Savings</span>
                            </div>
                            <span className="font-semibold">{formatCurrency(bankBalances.savings)}</span>
                          </div>
                        )}
                        {bankBalances.total === 0 && !status?.mercury?.connected && (
                          <div className="text-sm text-slate-500 p-3 bg-slate-50 rounded-lg">
                            Mercury not connected. Connect to see bank accounts.
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Mercury Total */}
                    {bankBalances.total > 0 && (
                      <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg border border-slate-200">
                        <div className="flex items-center gap-3">
                          <Building2 className="w-5 h-5 text-slate-700" />
                          <span className="text-sm font-semibold">Mercury Total</span>
                        </div>
                        <span className="font-bold text-slate-900">{formatCurrency(bankBalances.total)}</span>
                      </div>
                    )}
                    
                    {/* Stripe Balances */}
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-medium">Stripe Available</span>
                      </div>
                      <span className="font-semibold">{formatCurrency(stripeBalance.available)}</span>
                    </div>
                    {stripeBalance.instant_available > 0 && (
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <CreditCard className="w-5 h-5 text-green-600" />
                          <span className="text-sm font-medium">Stripe Instant Available</span>
                        </div>
                        <span className="font-semibold text-green-700">{formatCurrency(stripeBalance.instant_available)}</span>
                      </div>
                    )}
                    {stripeBalance.pending > 0 && (
                      <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Clock className="w-5 h-5 text-amber-600" />
                          <span className="text-sm font-medium">Stripe Pending</span>
                        </div>
                        <span className="font-semibold text-amber-700">{formatCurrency(stripeBalance.pending)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'timeline' && (
              <div>
                {/* Timeline Filter */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                  <div>
                    <h3 className="font-semibold text-slate-900">Daily Cash Flow Timeline</h3>
                    <p className="text-sm text-slate-500">Click any day to see details</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {[7, 14, 30, 60, 90].map(days => (
                      <button
                        key={days}
                        onClick={() => {
                          setTimelineFilter(days);
                          fetchDailyProjections(days);
                        }}
                        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                          timelineFilter === days
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {days}d
                      </button>
                    ))}
                  </div>
                </div>

                {/* Summary Stats */}
                {dailyProjections?.summary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Total Incoming</p>
                      <p className="text-lg font-semibold text-green-600">{formatCurrency(dailyProjections.summary.total_incoming)}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Total Outgoing</p>
                      <p className="text-lg font-semibold text-red-600">{formatCurrency(dailyProjections.summary.total_outgoing + dailyProjections.summary.total_recurring)}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Lowest Balance</p>
                      <p className={`text-lg font-semibold ${dailyProjections.summary.min_balance < 0 ? 'text-red-600' : 'text-slate-900'}`}>
                        {formatCurrency(dailyProjections.summary.min_balance)}
                      </p>
                      <p className="text-xs text-slate-500">{formatDate(dailyProjections.summary.min_balance_date)}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Net Change</p>
                      <p className={`text-lg font-semibold ${dailyProjections.summary.net_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {dailyProjections.summary.net_change >= 0 ? '+' : ''}{formatCurrency(dailyProjections.summary.net_change)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Daily Timeline */}
                <div className="space-y-1 max-h-[600px] overflow-y-auto">
                  {dailyProjections?.projections?.map((day, idx) => (
                    <div key={day.date} className="border rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedDay(expandedDay === day.date ? null : day.date)}
                        className={`w-full flex items-center justify-between p-3 text-left transition-colors ${
                          day.has_events ? 'hover:bg-slate-50' : ''
                        } ${
                          idx === 0 ? 'bg-blue-50' : 
                          day.closing_balance < 0 ? 'bg-red-50' :
                          day.closing_balance < 10000 ? 'bg-amber-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-center w-14">
                            <p className="text-xs text-slate-500">{day.day_of_week}</p>
                            <p className="text-sm font-medium">{new Date(day.date).getDate()}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            {day.incoming > 0 && (
                              <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded">
                                +{formatCurrency(day.incoming)}
                              </span>
                            )}
                            {(day.outgoing > 0 || day.recurring > 0) && (
                              <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded">
                                -{formatCurrency(day.outgoing + day.recurring)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Balance</p>
                            <p className={`text-sm font-semibold ${day.closing_balance < 0 ? 'text-red-600' : 'text-slate-900'}`}>
                              {formatCurrency(day.closing_balance)}
                            </p>
                          </div>
                          {day.has_events && (
                            <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expandedDay === day.date ? 'rotate-90' : ''}`} />
                          )}
                        </div>
                      </button>
                      
                      {/* Expanded Day Details */}
                      {expandedDay === day.date && day.has_events && (
                        <div className="border-t bg-slate-50 p-3 space-y-2">
                          {day.events.incoming.map((event, i) => (
                            <div key={`in-${i}`} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <ArrowDownRight className="w-4 h-4 text-green-600" />
                                <span>{event.description}</span>
                                <span className="text-xs px-1.5 py-0.5 bg-slate-200 rounded">{event.source}</span>
                              </div>
                              <span className="font-medium text-green-600">+{formatCurrency(event.amount)}</span>
                            </div>
                          ))}
                          {day.events.outgoing.map((event, i) => (
                            <div key={`out-${i}`} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <ArrowUpRight className="w-4 h-4 text-red-600" />
                                <span>{event.description}</span>
                                <span className="text-xs px-1.5 py-0.5 bg-slate-200 rounded">{event.source}</span>
                              </div>
                              <span className="font-medium text-red-600">-{formatCurrency(event.amount)}</span>
                            </div>
                          ))}
                          {day.events.recurring.map((event, i) => (
                            <div key={`rec-${i}`} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <Receipt className="w-4 h-4 text-purple-600" />
                                <span>{event.description}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${getCategoryColor(event.category)}`}>{event.category}</span>
                              </div>
                              <span className="font-medium text-red-600">-{formatCurrency(event.amount)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'incoming' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Incoming Payments</h3>
                  <span className="text-sm text-slate-500">{incomingPayments.length} pending</span>
                </div>
                {incomingPayments.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No pending incoming payments</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider border-b">
                          <th className="pb-3 pr-4">Customer</th>
                          <th className="pb-3 pr-4">Project</th>
                          <th className="pb-3 pr-4">Amount</th>
                          <th className="pb-3 pr-4">Expected</th>
                          <th className="pb-3">Source</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {incomingPayments.map((payment) => (
                          <tr key={payment.id} className="text-sm">
                            <td className="py-3 pr-4 font-medium text-slate-900">{payment.customer_name}</td>
                            <td className="py-3 pr-4 text-slate-600">{payment.project_name || '—'}</td>
                            <td className="py-3 pr-4 font-medium text-green-600">{formatCurrency(payment.amount)}</td>
                            <td className="py-3 pr-4 text-slate-600">{formatDate(payment.expected_date)}</td>
                            <td className="py-3">
                              <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-700">
                                {payment.source}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'outgoing' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Outgoing Payments</h3>
                  <span className="text-sm text-slate-500">{outgoingPayments.length} pending</span>
                </div>
                {outgoingPayments.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No pending outgoing payments</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider border-b">
                          <th className="pb-3 pr-4">Vendor</th>
                          <th className="pb-3 pr-4">Invoice</th>
                          <th className="pb-3 pr-4">Amount</th>
                          <th className="pb-3 pr-4">Due Date</th>
                          <th className="pb-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {outgoingPayments.map((payment) => (
                          <tr key={payment.id} className="text-sm">
                            <td className="py-3 pr-4 font-medium text-slate-900">{payment.vendor_name}</td>
                            <td className="py-3 pr-4 text-slate-600">{payment.invoice_number || '—'}</td>
                            <td className="py-3 pr-4 font-medium text-red-600">{formatCurrency(payment.amount)}</td>
                            <td className="py-3 pr-4 text-slate-600">{formatDate(payment.due_date)}</td>
                            <td className="py-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                payment.status === 'sent_to_mercury' ? 'bg-green-100 text-green-800' :
                                payment.status === 'queued' ? 'bg-blue-100 text-blue-800' :
                                'bg-amber-100 text-amber-800'
                              }`}>
                                {payment.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'recurring' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Recurring Expenses</h3>
                  <Button onClick={() => setShowAddExpense(true)} size="sm" className="gap-2" data-testid="add-expense-btn">
                    <Plus className="w-4 h-4" /> Add Expense
                  </Button>
                </div>

                {/* Suggestions - Enhanced with edit-before-confirm */}
                {suggestions.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <h4 className="text-sm font-medium text-slate-700">Auto-Detected Patterns</h4>
                      <span className="text-xs text-slate-500">({suggestions.length} found)</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-3">
                      We detected these recurring transactions from your Mercury history. Review and confirm to include them in your cash flow projections.
                    </p>
                    <div className="space-y-3">
                      {suggestions.map((expense) => (
                        <div key={expense.id} className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl" data-testid={`suggestion-${expense.id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(expense.category)}`}>
                                  {expense.category}
                                </span>
                                <span className="text-xs text-slate-500 capitalize">{expense.frequency}</span>
                              </div>
                              <h5 className="font-semibold text-slate-900 mb-1">{expense.name}</h5>
                              <div className="flex items-center gap-4 text-sm">
                                <span className="font-medium text-slate-700">{formatCurrency(expense.amount)}</span>
                                <span className="text-slate-500">Next: {formatDate(expense.next_due_date)}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={() => openConfirmModal(expense)}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                                title="Review & Confirm"
                                data-testid={`confirm-btn-${expense.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                                Confirm
                              </button>
                              <button
                                onClick={() => dismissExpense(expense.id)}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
                                title="Not a recurring expense"
                                data-testid={`dismiss-btn-${expense.id}`}
                              >
                                <X className="w-4 h-4" />
                                Dismiss
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Confirmed Expenses */}
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Confirmed Recurring Expenses</h4>
                  {recurringExpenses.filter(e => e.confirmed).length === 0 ? (
                    <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                      <Receipt className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-slate-500">No recurring expenses confirmed yet</p>
                      <p className="text-xs text-slate-400 mt-1">Confirm detected patterns above or add manually</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="confirmed-expenses-table">
                        <thead>
                          <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider border-b">
                            <th className="pb-3 pr-4">Name</th>
                            <th className="pb-3 pr-4">Category</th>
                            <th className="pb-3 pr-4">Amount</th>
                            <th className="pb-3 pr-4">Frequency</th>
                            <th className="pb-3 pr-4">Next Due</th>
                            <th className="pb-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {recurringExpenses.filter(e => e.confirmed).map((expense) => (
                            <tr key={expense.id} className="text-sm" data-testid={`expense-row-${expense.id}`}>
                              <td className="py-3 pr-4">
                                <div>
                                  <span className="font-medium text-slate-900">{expense.name}</span>
                                  {expense.auto_detected && (
                                    <span className="ml-2 text-xs text-slate-400">(auto-detected)</span>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 pr-4">
                                <span className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(expense.category)}`}>
                                  {expense.category}
                                </span>
                              </td>
                              <td className="py-3 pr-4 font-medium text-slate-900">{formatCurrency(expense.amount)}</td>
                              <td className="py-3 pr-4 text-slate-600 capitalize">{expense.frequency}</td>
                              <td className="py-3 pr-4 text-slate-600">{formatDate(expense.next_due_date)}</td>
                              <td className="py-3">
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => openEditModal(expense)}
                                    className="p-1.5 text-blue-600 hover:bg-blue-100 rounded"
                                    title="Edit"
                                    data-testid={`edit-btn-${expense.id}`}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteExpense(expense.id)}
                                    className="p-1.5 text-red-600 hover:bg-red-100 rounded"
                                    title="Delete"
                                    data-testid={`delete-btn-${expense.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add Expense Modal */}
        {showAddExpense && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Add Recurring Expense</h3>
                <button onClick={() => setShowAddExpense(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={newExpense.name}
                    onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Office Rent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <select
                    value={newExpense.category}
                    onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="payroll">Payroll</option>
                    <option value="insurance">Insurance</option>
                    <option value="software">Software</option>
                    <option value="utilities">Utilities</option>
                    <option value="rent">Rent</option>
                    <option value="subscriptions">Subscriptions</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Frequency</label>
                  <select
                    value={newExpense.frequency}
                    onChange={(e) => setNewExpense({ ...newExpense, frequency: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Biweekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Next Due Date *</label>
                  <input
                    type="date"
                    value={newExpense.next_due_date}
                    onChange={(e) => setNewExpense({ ...newExpense, next_due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Vendor (optional)</label>
                  <input
                    type="text"
                    value={newExpense.vendor_name}
                    onChange={(e) => setNewExpense({ ...newExpense, vendor_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Duke Energy"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowAddExpense(false)}>Cancel</Button>
                <Button onClick={addManualExpense}>Add Expense</Button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Suggestion Modal */}
        {confirmingExpense && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="confirm-modal">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Confirm Recurring Expense</h3>
                  <p className="text-sm text-slate-500">Review and adjust details before confirming</p>
                </div>
                <button onClick={() => setConfirmingExpense(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Auto-detected badge */}
              <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4" />
                  <span>Auto-detected from Mercury transactions</span>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={confirmForm.name}
                    onChange={(e) => setConfirmForm({ ...confirmForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="confirm-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <select
                    value={confirmForm.category}
                    onChange={(e) => setConfirmForm({ ...confirmForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="confirm-category-select"
                  >
                    <option value="payroll">Payroll</option>
                    <option value="insurance">Insurance</option>
                    <option value="software">Software</option>
                    <option value="utilities">Utilities</option>
                    <option value="rent">Rent</option>
                    <option value="subscriptions">Subscriptions</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={confirmForm.amount}
                        onChange={(e) => setConfirmForm({ ...confirmForm, amount: e.target.value })}
                        className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="confirm-amount-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Frequency</label>
                    <select
                      value={confirmForm.frequency}
                      onChange={(e) => setConfirmForm({ ...confirmForm, frequency: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="confirm-frequency-select"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Biweekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Next Due Date</label>
                  <input
                    type="date"
                    value={confirmForm.next_due_date}
                    onChange={(e) => setConfirmForm({ ...confirmForm, next_due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="confirm-date-input"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setConfirmingExpense(null)}>Cancel</Button>
                <Button onClick={confirmExpenseWithEdits} className="bg-green-600 hover:bg-green-700" data-testid="confirm-save-btn">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Confirm Expense
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Expense Modal */}
        {editingExpense && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-modal">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Edit Recurring Expense</h3>
                  <p className="text-sm text-slate-500">Update expense details</p>
                </div>
                <button onClick={() => setEditingExpense(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={confirmForm.name}
                    onChange={(e) => setConfirmForm({ ...confirmForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="edit-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <select
                    value={confirmForm.category}
                    onChange={(e) => setConfirmForm({ ...confirmForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="edit-category-select"
                  >
                    <option value="payroll">Payroll</option>
                    <option value="insurance">Insurance</option>
                    <option value="software">Software</option>
                    <option value="utilities">Utilities</option>
                    <option value="rent">Rent</option>
                    <option value="subscriptions">Subscriptions</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={confirmForm.amount}
                        onChange={(e) => setConfirmForm({ ...confirmForm, amount: e.target.value })}
                        className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="edit-amount-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Frequency</label>
                    <select
                      value={confirmForm.frequency}
                      onChange={(e) => setConfirmForm({ ...confirmForm, frequency: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="edit-frequency-select"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Biweekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Next Due Date</label>
                  <input
                    type="date"
                    value={confirmForm.next_due_date}
                    onChange={(e) => setConfirmForm({ ...confirmForm, next_due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="edit-date-input"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setEditingExpense(null)}>Cancel</Button>
                <Button onClick={saveExpenseEdits} data-testid="edit-save-btn">
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminSidebar>
  );
}
